import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Form } from 'react-bootstrap';
import { Button } from "@app/components";
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { setStep } from '@app/store/reducers/enrollmentForm';
import { getActiveTemplateByType } from '@app/services/contentTemplates';

const EnrollmentStep5 = (props) => {
        
    const enrollmentStore = useSelector((state: any) => state.enrollmentForm);
    const [isLoading, setLoading] = useState(false);
    const [templateContent, setTemplateContent] = useState<any>(null);
    const [loadingTemplate, setLoadingTemplate] = useState(true);
    const dispatch = useDispatch();
    
    useEffect(() => {
        fetchTermsTemplate();
        return () => {

        }
    },[]);

    const fetchTermsTemplate = async () => {
        try {
            setLoadingTemplate(true);
            const franchiseId = enrollmentStore?.formData?.franchise_id;
            if (franchiseId) {
                const template = await getActiveTemplateByType(franchiseId, 'terms_and_conditions');
                if (template) {
                    setTemplateContent(template);
                }
            }
        } catch (error) {
            console.error('Error fetching terms template:', error);
        } finally {
            setLoadingTemplate(false);
        }
    };

    const renderTemplateContent = () => {
        if (!templateContent) return null;

        try {
            const parsed = JSON.parse(templateContent.content);
            let htmlContent = parsed.htmlContent || '';
            
            // Replace all placeholders with their values from parsed content
            Object.keys(parsed).forEach(key => {
                if (key !== 'htmlContent' && parsed[key] !== undefined && parsed[key] !== '') {
                    let replacementValue = parsed[key];
                    
                    // Special handling for day-of-every-month - add ordinal suffix
                    if (key === 'day-of-every-month' && !isNaN(replacementValue)) {
                        const num = parseInt(replacementValue);
                        const j = num % 10;
                        const k = num % 100;
                        
                        if (j === 1 && k !== 11) {
                            replacementValue = num + '<sup>st</sup>';
                        } else if (j === 2 && k !== 12) {
                            replacementValue = num + '<sup>nd</sup>';
                        } else if (j === 3 && k !== 13) {
                            replacementValue = num + '<sup>rd</sup>';
                        } else {
                            replacementValue = num + '<sup>th</sup>';
                        }
                    }
                    
                    const regex = new RegExp(`\\[${key}\\]`, 'g');
                    htmlContent = htmlContent.replace(regex, replacementValue);
                }
            });
            
            return (
                <div 
                    className="ql-editor" 
                    dangerouslySetInnerHTML={{ __html: htmlContent }}
                    style={{ padding: 0 }}
                />
            );
        } catch (error) {
            return (
                <div 
                    className="ql-editor" 
                    dangerouslySetInnerHTML={{ __html: templateContent.content }}
                    style={{ padding: 0 }}
                />
            );
        }
    };
    

    const { handleChange, values, handleSubmit, touched, errors, setFieldValue } = useFormik({
        initialValues: {
            step: 5,
            agreed_terms: enrollmentStore?.formData?.agreed_terms || false,
            understood_fees: enrollmentStore?.formData?.understood_fees || false,
            accepted_policies: enrollmentStore?.formData?.accepted_policies || false,
        },
        validationSchema: Yup.object({
            agreed_terms:  Yup.bool().oneOf([true], 'You need to accept the terms and conditions'),
            understood_fees: Yup.bool().oneOf([true], 'You need to accept the fee payment structure and holidays policy.'),
            accepted_policies: Yup.bool().oneOf([true], 'You need to accept the discontinuation policy, privacy policy, and the behavioural guidelines.'),
        }),
        onSubmit: (values) => {
            props?.updateEnrollmentDetailsAPI(values)
        },
        enableReinitialize: true,
    });

    return (
        <>
        <Form onSubmit={handleSubmit}>
        <div className="content mb-3">
            <h6 className="grey_wrapper_title">
                <i className="fas fa-file-signature"/> &nbsp; 
                {templateContent?.title || 'First Class Learning Terms And Conditions For Parents'}
            </h6>
            {loadingTemplate ? (
                <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                        <span className="sr-only">Loading...</span>
                    </div>
                </div>
            ) : templateContent ? (
                <div className="">
                    {renderTemplateContent()}
                </div>
            ) : (
                <div className="alert alert-danger">
                    <i className="fas fa-exclamation-triangle mr-2"></i>
                    We're unable to load the required information at this time. Please contact us for assistance or try again later.
                </div>
            )}
            <div className='row'>
                <div className="col-sm-12">
                    <div className="icheck-primary">
                        <input 
                            type="checkbox" 
                            className="form-check-input" 
                            checked={values.agreed_terms}
                            id="agreed_terms"
                            name="agreed_terms"
                            onChange={ handleChange }
                            disabled={!templateContent}
                        />
                        <label htmlFor="agreed_terms" className="form-check-label">
                            I confirm that I have read and agree to the Terms and Conditions of the Tuition Centre.
                        </label>
                    </div>
                    {(touched.agreed_terms && errors.agreed_terms) && (
                        <div className="text-danger">
                            {errors.agreed_terms}
                        </div>
                    )}
                </div>
                <div className="col-sm-12">
                    <div className="icheck-primary">
                        <input 
                            type="checkbox" 
                            className="form-check-input" 
                            checked={values.understood_fees}
                            id="understood_fees"
                            name="understood_fees"
                            onChange={ handleChange }
                            disabled={!templateContent}
                        />
                        <label htmlFor="understood_fees" className="form-check-label">
                            I understand the fee payment structure and holidays policy.
                        </label>
                    </div>
                    {(touched.understood_fees && errors.understood_fees) && (
                        <div className="text-danger">
                            {errors.understood_fees}
                        </div>
                    )}
                </div>
                <div className="col-sm-12">
                    <div className="icheck-primary">
                        <input 
                            type="checkbox" 
                            className="form-check-input" 
                            checked={values.accepted_policies}
                            id="accepted_policies"
                            name="accepted_policies"
                            onChange={ handleChange }
                            disabled={!templateContent}
                        />
                        <label htmlFor="accepted_policies" className="form-check-label">
                            I agree to the discontinuation policy, privacy policy, and the behavioural guidelines.
                        </label>
                    </div>
                    {(touched.accepted_policies && errors.accepted_policies) && (
                        <div className="text-danger">
                            {errors.accepted_policies}
                        </div>
                    )}
                </div>
            </div>
        </div>
        <div className="mx-4 d-flex justify-content-between align-items-center">
            <Button type="button" isLoading={isLoading} onClick={ () => dispatch(setStep(enrollmentStore?.step - 1))} className="btn btn-default"> Back </Button>
            <Button type="submit" isLoading={isLoading} className="btn btn-success" disabled={!templateContent || loadingTemplate}> Submit </Button>
        </div>
        </Form>
        </>
  );
};

export default EnrollmentStep5;
